/**
 * Density context internals — shared by `DensityProvider` (the component,
 * in DensityProvider.tsx) and the `useDensity()` consumer hook. Split out
 * so the provider file exports only components (react-refresh contract).
 */
import { createContext, useContext } from 'react';

export type Density = 'compact' | 'cozy' | 'comfortable';

export interface DensityTokens {
  rowHeight: string;
  buttonHeight: string;
  fontSizeBody: string;
  spacingY: string;
}

export const DENSITY_TOKENS: Record<Density, DensityTokens> = {
  compact:     { rowHeight: '28px', buttonHeight: '24px', fontSizeBody: '12px', spacingY: '4px' },
  cozy:        { rowHeight: '40px', buttonHeight: '32px', fontSizeBody: '14px', spacingY: '8px' },
  comfortable: { rowHeight: '48px', buttonHeight: '40px', fontSizeBody: '14px', spacingY: '12px' },
};

export interface DensityContextValue {
  density: Density;
  tokens: DensityTokens;
  setDensity: (d: Density) => void;
}

export const DensityContext = createContext<DensityContextValue | null>(null);

export function useDensity(): DensityContextValue {
  const ctx = useContext(DensityContext);
  if (!ctx) {
    /* Permissive fallback — allows components to render outside provider. */
    return {
      density: 'cozy',
      tokens: DENSITY_TOKENS.cozy,
      setDensity: () => {},
    };
  }
  return ctx;
}
